import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, Mail, Truck, Landmark, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, fetchOrder, formatGBP } from "../lib/api";
import { useSite } from "../context/SiteContext";
import AcceptedPaymentsRow from "../components/AcceptedPaymentsRow";
import { useSEO } from "../hooks/useSEO";

const OrderConfirmation = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const accessToken = searchParams.get("token") || "";
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);
  const [paying, setPaying] = useState(false);
  const site = useSite();

  useSEO({
    title: "Order confirmation",
    description: "Sterling Bio order confirmation & Pay-by-Bank checkout.",
    path: `/order/${id}`,
  });

  useEffect(() => {
    fetchOrder(id, accessToken)
      .then(setOrder)
      .catch(() => setError("Order not found."));
  }, [id, accessToken]);

  const payByBank = async () => {
    setPaying(true);
    try {
      const r = await api.post("/payments/wallid/create", {
        order_id: id,
        access_token: accessToken,
      });
      if (r.data?.already_paid) {
        toast.success("Order already paid.");
        setPaying(false);
        return;
      }
      const link = r.data?.payment_link;
      if (!link) {
        toast.error("Could not start Pay-by-Bank. Please contact support.");
        setPaying(false);
        return;
      }
      // Full-page redirect to the Wallid hosted checkout.
      window.location.href = link;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Payment provider error. Please try again.");
      setPaying(false);
    }
  };

  if (error)
    return (
      <div data-testid="order-error" className="max-w-2xl mx-auto py-24 text-center">
        <p className="text-[#B91C1C]">{error}</p>
      </div>
    );

  if (!order)
    return <div className="max-w-2xl mx-auto py-24 text-center text-[#64748B]">Loading…</div>;

  const isPaid = order.status === "completed";

  return (
    <div data-testid="order-page" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="flex items-center gap-3 text-[#047857] mb-4">
        <CheckCircle2 className="w-6 h-6" />
        <span className="sb-mono-label !text-[#047857]">Order received</span>
      </div>
      <h1
        className="text-3xl sm:text-4xl font-semibold text-[#1B3A5C] tracking-tight"
        style={{ fontFamily: "Outfit" }}
      >
        Thanks {order.full_name?.split(" ")[0] || "there"} — your order is{" "}
        <span data-testid="order-number" className="font-mono text-[#2C8C9C]">
          {order.order_number}
        </span>
      </h1>
      <p className="mt-3 text-[#64748B] text-sm">
        A confirmation email is on the way to{" "}
        <span className="font-mono text-[#1B3A5C]">{order.email}</span>. Same-day UK dispatch
        after payment.
      </p>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-5 gap-8">
        {/* Left — order items */}
        <div className="lg:col-span-3 border border-[#E2E8F0] bg-white p-6">
          <div className="sb-mono-label mb-4">Order items</div>
          <table className="w-full text-sm">
            <tbody>
              {order.items.map((it) => (
                <tr
                  key={`${it.product_id}-${it.sku}`}
                  className="border-b border-[#E2E8F0] last:border-b-0"
                >
                  <td className="py-3">
                    <div className="text-[#1B3A5C] font-medium">{it.name}</div>
                    <div className="font-mono text-xs text-[#64748B]">
                      {it.sku} · {it.dosage} · x{it.quantity}
                    </div>
                  </td>
                  <td className="py-3 text-right font-mono text-[#1B3A5C]">
                    {formatGBP(it.line_total_gbp)}
                  </td>
                </tr>
              ))}
              <tr className="border-t-2 border-[#1B3A5C]">
                <td className="py-3 font-mono uppercase text-xs text-[#64748B] tracking-wider">Total</td>
                <td className="py-3 text-right font-mono text-lg text-[#1B3A5C]">
                  {formatGBP(order.total_gbp)}
                </td>
              </tr>
            </tbody>
          </table>

          <div className="mt-6 pt-6 border-t border-[#E2E8F0] space-y-2 text-xs font-mono uppercase tracking-wider text-[#64748B]">
            <div className="flex items-center gap-2">
              <Truck className="w-3.5 h-3.5 text-[#2C8C9C]" /> Dispatched same-day (UK) after payment
              received
            </div>
            <div className="flex items-center gap-2">
              <Mail className="w-3.5 h-3.5 text-[#2C8C9C]" /> Support · {site?.support_email || "Info@sterlingbio.co.uk"}
            </div>
          </div>
        </div>

        {/* Right — pay by bank */}
        <div className="lg:col-span-2 border border-[#E2E8F0] bg-white p-6">
          <div className="sb-mono-label mb-4">Payment</div>
          {isPaid ? (
            <div
              data-testid="already-paid"
              className="border border-[#047857]/30 bg-[#ECFDF5] p-4 text-sm text-[#065F46]"
            >
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle2 className="w-4 h-4" />
                <span className="font-mono uppercase tracking-wider text-xs">Payment received</span>
              </div>
              Your order is being dispatched.
            </div>
          ) : (
            <>
              <p className="text-sm text-[#475569]">
                Pay securely from your bank account via Open Banking. You&apos;ll be redirected to
                your bank&apos;s app to approve the payment — no card details are entered.
              </p>
              <div className="my-4 flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-[#64748B]">
                <Landmark className="w-3.5 h-3.5 text-[#2C8C9C]" /> UK bank transfer · Faster Payments
              </div>
              <button
                data-testid="pay-by-bank-btn"
                onClick={payByBank}
                disabled={paying}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#1B3A5C] hover:bg-[#2C8C9C] disabled:opacity-60 text-white px-5 py-3 text-sm font-medium tracking-wide transition-colors"
              >
                {paying ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Redirecting…
                  </>
                ) : (
                  <>Pay {formatGBP(order.total_gbp)} by bank</>
                )}
              </button>
              <div className="mt-4 pt-4 border-t border-[#E2E8F0]">
                <AcceptedPaymentsRow />
              </div>
              <p className="mt-3 text-[10px] font-mono uppercase tracking-wider text-[#64748B]">
                Payment processed by Wallid via UK Open Banking. Faster Payments settle within
                seconds; some banks confirm in up to 2 hours.
              </p>
            </>
          )}
        </div>
      </div>

      <div className="mt-10 text-center">
        <Link to="/products" className="sb-link-underline text-[#2C8C9C] text-sm">
          ← Continue browsing
        </Link>
      </div>
    </div>
  );
};

export default OrderConfirmation;
